// Accept Invitation Page

import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { trpc } from '../../lib/trpc';
import { useAuthStore } from '../../stores/auth-store';

interface AcceptInviteFormData {
  password: string;
  confirmPassword: string;
  displayName?: string;
}

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { login } = useAuthStore();
  const [success, setSuccess] = useState(false);

  // Get invitation info
  const invitationQuery = trpc.auth.getInvitationInfo.useQuery(
    { token: token ?? '' },
    { enabled: !!token, retry: false }
  );

  // Accept invitation mutation
  const acceptMutation = trpc.auth.acceptInvitation.useMutation({
    onSuccess: (data) => {
      login(data.accessToken, data.refreshToken, data.user, data.expiresAt);
      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AcceptInviteFormData>();

  const password = watch('password');

  const onSubmit = (data: AcceptInviteFormData) => {
    if (!token) return;
    acceptMutation.mutate({
      token,
      password: data.password,
      displayName: data.displayName,
    });
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Link</h2>
          <p className="text-gray-600 mb-4">
            This invitation link is missing the required token.
          </p>
          <Link to="/login" className="text-primary-600 hover:text-primary-700">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  // Loading
  if (invitationQuery.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading invitation...</div>
      </div>
    );
  }

  // Invalid or expired invitation
  if (invitationQuery.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Invitation</h2>
          <p className="text-gray-600 mb-4">
            {invitationQuery.error.message || 'This invitation link is invalid or has expired.'}
          </p>
          <Link to="/login" className="text-primary-600 hover:text-primary-700">
            Go to login
          </Link>
        </div>
      </div>
    );
  }

  const invitation = invitationQuery.data;

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full text-center">
          <div className="rounded-full bg-green-100 w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome to {invitation?.tenantName}!</h2>
          <p className="text-gray-600">Redirecting to your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src="/calculom.png" alt="Calculom" className="w-[110px] h-auto mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">Join {invitation?.tenantName}</h2>
          <p className="mt-2 text-gray-600">
            You've been invited to join as a{' '}
            <span className="font-medium capitalize">{invitation?.role?.replace('_', ' ')}</span>
          </p>
          <p className="mt-1 text-sm text-gray-500">
            {invitation?.email}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6">
          {acceptMutation.error && (
            <div className="rounded-lg bg-red-50 p-4 text-sm text-red-600">
              {acceptMutation.error.message}
            </div>
          )}

          {!invitation?.hasExistingAccount && (
            <div>
              <label htmlFor="displayName" className="label">
                Your Name (optional)
              </label>
              <input
                id="displayName"
                type="text"
                autoComplete="name"
                className="input"
                placeholder="John Smith"
                {...register('displayName')}
              />
            </div>
          )}

          <div>
            <label htmlFor="password" className="label">
              {invitation?.hasExistingAccount ? 'Confirm Password' : 'Create Password'}
            </label>
            <input
              id="password"
              type="password"
              autoComplete="new-password"
              className="input"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Password must contain uppercase, lowercase, and a number',
                },
              })}
            />
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="label">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              className="input"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (value) => value === password || 'Passwords do not match',
              })}
            />
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={acceptMutation.isPending}
            className="btn-primary w-full"
          >
            {acceptMutation.isPending ? 'Joining...' : 'Accept Invitation'}
          </button>
        </form>

        <div className="text-center text-sm text-gray-500">
          Already have an account?{' '}
          <Link to="/login" className="text-primary-600 hover:text-primary-700">
            Log in instead
          </Link>
        </div>
      </div>
    </div>
  );
}
